// NEW FILE — "WhatsApp Workflows" page.
// Same fixed-pipeline pattern as pages/workflows/EmailWorkflow.js
// (Webhook trigger -> Recipients -> Send message), but:
//   1. sends via WhatsApp (Meta Cloud API) instead of SMTP
//   2. supports every targeting mode from the Push Notification module's
//      TargetSelector pattern, expanded to also cover raw bulk numbers:
//        organization | division | department | single | selected | bulk
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  createWhatsAppWorkflow,
  listWhatsAppWorkflows,
  deleteWhatsAppWorkflow,
  updateWhatsAppWorkflow,
  testRunWhatsAppWorkflow,
  listWhatsAppWorkflowExecutions,
} from "../../services/WhatsappWorkflowApi";
import { getDepartments, getDivisions, getUsers } from "../../services/PushNotificationApi";
import SummaryApi from "../../common/SummaryApi";

// Same backend-domain resolution logic used in EmailWorkflow.js / common/SummaryApi.js
const isLocalNetwork = window.location.hostname !== "localhost" && window.location.hostname !== "127.0.0.1";
const backendDomain =
  process.env.NODE_ENV === "development"
    ? isLocalNetwork
      ? `http://${window.location.hostname}:5000`
      : "http://localhost:5000"
    : process.env.NODE_ENV === "production"
    ? "https://apd-group-biz.onrender.com"
    : "http://192.168.0.15:5000";

const TARGET_TYPES = [
  { value: "organization", label: "Entire Organization" },
  { value: "division", label: "Division" },
  { value: "department", label: "Department" },
  { value: "single", label: "Single User" },
  { value: "selected", label: "Selected Users (Bulk Pick)" },
  { value: "bulk", label: "Bulk Numbers (paste/upload)" },
];

const emptyForm = {
  name: "",
  description: "",
  targetType: "organization",
  divisionId: "",
  departmentId: "",
  userId: "",
  userIds: [],
  numbersText: "", // raw textarea, one number per line / comma separated
  onlyActive: true,
  messageType: "text",
  message: "",
  templateName: "",
  languageCode: "en_US",
};

const parseNumbers = (text) =>
  (text || "")
    .split(/[\n,]/)
    .map((n) => n.trim())
    .filter(Boolean);

const WhatsAppWorkflows = () => {
  const [loading, setLoading] = useState(true);
  const [workflows, setWorkflows] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [users, setUsers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [runningId, setRunningId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [executions, setExecutions] = useState({});
  const [editingId, setEditingId] = useState(null);

  useEffect(() => {
   
    loadAll();
  }, []);

const loadAll = async () => {
  

  try {
    setLoading(true);

  
    const wfRes = await listWhatsAppWorkflows();
  

    
    const deptRes = await getDepartments();
   

   
    const divRes = await getDivisions();
   

    
    const usersRes = await getUsers();
    
    setWorkflows(wfRes.data || []);
    setDepartments(deptRes.data || []);
    setDivisions(divRes.data || []);
    setUsers(usersRes.data || []);
  } catch (err) {
    console.error("loadAll Error:", err);
  } finally {
    setLoading(false);
  }

};

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === "checkbox" ? checked : value }));
  };

  const toggleSelectedUser = (userId) => {
    setForm((f) => {
      const already = f.userIds.includes(userId);
      return {
        ...f,
        userIds: already ? f.userIds.filter((id) => id !== userId) : [...f.userIds, userId],
      };
    });
  };

  const buildPayload = (f) => ({
    name: f.name,
    description: f.description,
    targetType: f.targetType,
    target: {
      divisionId: f.targetType === "division" ? f.divisionId : undefined,
      departmentId: f.targetType === "department" ? f.departmentId : undefined,
      userId: f.targetType === "single" ? f.userId : undefined,
      userIds: f.targetType === "selected" ? f.userIds : undefined,
      numbers: f.targetType === "bulk" ? parseNumbers(f.numbersText) : undefined,
      onlyActive: f.onlyActive,
    },
    messageType: f.messageType,
    message: f.messageType === "text" ? f.message : undefined,
    templateName: f.messageType === "template" ? f.templateName : undefined,
    languageCode: f.messageType === "template" ? f.languageCode : undefined,
  });

  const validate = (f) => {
    if (!f.name) return "Workflow name is required";
    if (f.messageType === "text" && !f.message) return "Message text is required";
    if (f.messageType === "template" && !f.templateName) return "Template name is required";
    if (f.targetType === "division" && !f.divisionId) return "Please select a division";
    if (f.targetType === "department" && !f.departmentId) return "Please select a department";
    if (f.targetType === "single" && !f.userId) return "Please select a user";
    if (f.targetType === "selected" && f.userIds.length === 0) return "Please select at least one user";
    if (f.targetType === "bulk" && parseNumbers(f.numbersText).length === 0) return "Please add at least one phone number";
    return null;
  };

  const handleCreate = async (e) => {
    e.preventDefault();

    const error = validate(form);
    if (error) {
      toast.error(error);
      return;
    }

    try {
      setSaving(true);
      const payload = buildPayload(form);

      if (editingId) {
        await updateWhatsAppWorkflow(editingId, payload);
        toast.success("Workflow updated");
      } else {
        await createWhatsAppWorkflow(payload);
        toast.success("Workflow created");
      }

      setForm(emptyForm);
      setEditingId(null);
      setShowForm(false);
      loadAll();
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.message ||
          (editingId ? "Failed to update workflow" : "Failed to create workflow")
      );
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (wf) => {
    setEditingId(wf._id);
    setForm({
      name: wf.name || "",
      description: wf.description || "",
      targetType: wf.targetType || "organization",
      divisionId: wf.target?.divisionId || "",
      departmentId: wf.target?.departmentId || "",
      userId: wf.target?.userId || "",
      userIds: wf.target?.userIds || [],
      numbersText: (wf.target?.numbers || []).join("\n"),
      onlyActive: wf.target?.onlyActive ?? true,
      messageType: wf.messageType || "text",
      message: wf.message || "",
      templateName: wf.templateName || "",
      languageCode: wf.languageCode || "en_US",
    });
    setShowForm(true);
  };

  const handleToggleActive = async (wf) => {
    try {
      await updateWhatsAppWorkflow(wf._id, { isActive: !wf.isActive });
      toast.success(`Workflow ${wf.isActive ? "deactivated" : "activated"}`);
      loadAll();
    } catch (err) {
      toast.error("Failed to update workflow");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this workflow?")) return;
    try {
      await deleteWhatsAppWorkflow(id);
      toast.success("Workflow deleted");
      loadAll();
    } catch (err) {
      toast.error("Failed to delete workflow");
    }
  };

  const handleTestRun = async (id) => {
    try {
      setRunningId(id);
      const res = await testRunWhatsAppWorkflow(id, {});
      if (res.success) {
        toast.success(
          `Sent to ${res.execution?.steps?.find((s) => s.nodeType === "whatsapp")?.output?.length || 0} recipient(s)`
        );
      } else {
        toast.error(res.execution?.error || "Workflow run failed");
      }
      loadExecutions(id);
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || "Test run failed");
    } finally {
      setRunningId(null);
    }
  };

  const loadExecutions = async (id) => {
    try {
      const res = await listWhatsAppWorkflowExecutions(id);
      setExecutions((prev) => ({ ...prev, [id]: res.data || [] }));
    } catch (err) {
      console.error(err);
    }
  };

  const toggleExpand = (id) => {
    if (expandedId === id) {
      setExpandedId(null);
      return;
    }
    setExpandedId(id);
    if (!executions[id]) loadExecutions(id);
  };
const copyWebhookUrl = (webhookId) => {
  const url = SummaryApi.whatsAppWebhook.url(webhookId);
  navigator.clipboard.writeText(url);
  toast.success("Webhook URL copied");
};

  if (loading) {
    return <div className="p-5">Loading...</div>;
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">WhatsApp Workflows</h1>
        <button
          onClick={() => setShowForm((s) => !s)}
          className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md text-sm font-medium"
        >
          {showForm ? "Cancel" : "+ New Workflow"}
        </button>
      </div>

      <p className="text-sm text-gray-500 mb-6">
        Each workflow runs a fixed pipeline: <strong>Webhook trigger</strong> →{" "}
        <strong>Recipients</strong> → <strong>WhatsApp (Meta Cloud API)</strong>. Call the
        workflow&apos;s webhook URL (or press &quot;Test Run&quot;) to message every matching
        recipient.
      </p>

      {showForm && (
        <form
          onSubmit={handleCreate}
          className="bg-white border border-gray-200 rounded-lg p-5 mb-8 shadow-sm space-y-4"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Workflow Name */}
            <div>
              <label className="block text-sm font-medium mb-1">Workflow Name *</label>
              <input
                name="name"
                value={form.name}
                onChange={handleChange}
                className="w-full border rounded-md px-3 py-2 text-sm"
                placeholder="e.g. Sprint Planning Reminder"
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium mb-1">Description</label>
              <input
                name="description"
                value={form.description}
                onChange={handleChange}
                className="w-full border rounded-md px-3 py-2 text-sm"
                placeholder="Optional"
              />
            </div>

            {/* Target Type */}
            <div>
              <label className="block text-sm font-medium mb-1">Send To *</label>
              <select
                name="targetType"
                value={form.targetType}
                onChange={handleChange}
                className="w-full border rounded-md px-3 py-2 text-sm"
              >
                {TARGET_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Only Active (irrelevant for bulk numbers) */}
            {form.targetType !== "bulk" && (
              <label className="flex items-center gap-2 text-sm mt-6">
                <input type="checkbox" name="onlyActive" checked={form.onlyActive} onChange={handleChange} />
                Only send to active users
              </label>
            )}

            {/* Division */}
            {form.targetType === "division" && (
              <div>
                <label className="block text-sm font-medium mb-1">Division *</label>
                <select
                  name="divisionId"
                  value={form.divisionId}
                  onChange={handleChange}
                  className="w-full border rounded-md px-3 py-2 text-sm"
                >
                  <option value="">Select Division</option>
                  {divisions.map((d) => (
                    <option key={d._id} value={d._id}>
                      {d.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Department */}
            {form.targetType === "department" && (
              <div>
                <label className="block text-sm font-medium mb-1">Department *</label>
                <select
                  name="departmentId"
                  value={form.departmentId}
                  onChange={handleChange}
                  className="w-full border rounded-md px-3 py-2 text-sm"
                >
                  <option value="">Select Department</option>
                  {departments.map((d) => (
                    <option key={d._id} value={d._id}>
                      {d.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Single User */}
            {form.targetType === "single" && (
              <div>
                <label className="block text-sm font-medium mb-1">User *</label>
                <select
                  name="userId"
                  value={form.userId}
                  onChange={handleChange}
                  className="w-full border rounded-md px-3 py-2 text-sm"
                >
                  <option value="">Select User</option>
                  {users.map((u) => (
                    <option key={u._id} value={u._id}>
                      {u.firstName} {u.lastName}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {/* Selected Users (multi-select checkboxes) */}
          {form.targetType === "selected" && (
            <div>
              <label className="block text-sm font-medium mb-1">
                Select Users * ({form.userIds.length} selected)
              </label>
              <div className="border rounded-md p-3 max-h-48 overflow-y-auto grid grid-cols-1 md:grid-cols-2 gap-1">
                {users.map((u) => (
                  <label key={u._id} className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={form.userIds.includes(u._id)}
                      onChange={() => toggleSelectedUser(u._id)}
                    />
                    {u.firstName} {u.lastName}
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Bulk numbers */}
          {form.targetType === "bulk" && (
            <div>
              <label className="block text-sm font-medium mb-1">
                Phone Numbers * (one per line, or comma separated)
              </label>
              <textarea
                name="numbersText"
                value={form.numbersText}
                onChange={handleChange}
                rows={4}
                className="w-full border rounded-md px-3 py-2 text-sm font-mono"
                placeholder={"9876543210\n9123456780"}
              />
              <p className="text-xs text-gray-400 mt-1">
                10-digit local numbers are auto-prefixed with the default country code
                (WHATSAPP_DEFAULT_COUNTRY_CODE). Include a country code directly for other countries.
              </p>
            </div>
          )}

          {/* Message Type */}
          <div>
            <label className="block text-sm font-medium mb-1">Message Type *</label>
            <select
              name="messageType"
              value={form.messageType}
              onChange={handleChange}
              className="w-full md:w-64 border rounded-md px-3 py-2 text-sm"
            >
              <option value="text">Free-form Text</option>
              <option value="template">Approved Template</option>
            </select>
            <p className="text-xs text-gray-400 mt-1">
              Free-form text only delivers within Meta&apos;s 24-hour customer-service window.
              Use an approved Template for the first message to a recipient.
            </p>
          </div>

          {form.messageType === "text" ? (
            <div>
              <label className="block text-sm font-medium mb-1">Message *</label>
              <textarea
                name="message"
                value={form.message}
                onChange={handleChange}
                rows={5}
                className="w-full border rounded-md px-3 py-2 text-sm font-mono"
                placeholder="Hi {{firstName}}, this is a reminder about..."
              />
              <p className="text-xs text-gray-400 mt-1">
                Available placeholders: {"{{firstName}}"}, {"{{lastName}}"}, {"{{name}}"},{" "}
                {"{{email}}"}, {"{{mobile}}"}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Template Name *</label>
                <input
                  name="templateName"
                  value={form.templateName}
                  onChange={handleChange}
                  className="w-full border rounded-md px-3 py-2 text-sm"
                  placeholder="e.g. meeting_reminder"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Language Code</label>
                <input
                  name="languageCode"
                  value={form.languageCode}
                  onChange={handleChange}
                  className="w-full border rounded-md px-3 py-2 text-sm"
                  placeholder="en_US"
                />
              </div>
            </div>
          )}

          <button
            type="submit"
            disabled={saving}
            className="bg-green-600 hover:bg-green-700 text-white px-5 py-2 rounded-md text-sm font-medium disabled:opacity-60"
          >
            {saving
              ? editingId
                ? "Updating..."
                : "Creating..."
              : editingId
              ? "Update Workflow"
              : "Create Workflow"}
          </button>
        </form>
      )}

      <div className="space-y-4">
        {workflows.length === 0 && (
          <div className="text-sm text-gray-400 border rounded-lg p-6 text-center">
            No WhatsApp workflows yet. Click &quot;+ New Workflow&quot; to create one.
          </div>
        )}

        {workflows.map((wf) => (
          <div key={wf._id} className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="font-semibold text-gray-800">{wf.name}</h3>
                  <span
                    className={`text-xs px-2 py-0.5 rounded-full ${
                      wf.isActive ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"
                    }`}
                  >
                    {wf.isActive ? "Active" : "Inactive"}
                  </span>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 capitalize">
                    {wf.targetType}
                  </span>
                </div>
                {wf.description && <p className="text-sm text-gray-500 mt-1">{wf.description}</p>}
                <div className="mt-2 text-xs text-gray-500 flex items-center gap-2">
                  <code className="bg-gray-100 px-2 py-1 rounded">
                    POST {backendDomain}/api/whatsapp-webhook/{wf.webhookId}
                  </code>
                  <button
                    onClick={() => copyWebhookUrl(wf.webhookId)}
                    className="text-green-600 hover:underline"
                  >
                    Copy
                  </button>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleTestRun(wf._id)}
                  disabled={runningId === wf._id}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-md text-xs font-medium disabled:opacity-60"
                >
                  {runningId === wf._id ? "Sending..." : "Test Run"}
                </button>
                <button
                  onClick={() => handleToggleActive(wf)}
                  className="border px-3 py-1.5 rounded-md text-xs font-medium"
                >
                  {wf.isActive ? "Deactivate" : "Activate"}
                </button>
                <button
                  onClick={() => toggleExpand(wf._id)}
                  className="border px-3 py-1.5 rounded-md text-xs font-medium"
                >
                  {expandedId === wf._id ? "Hide Runs" : "View Runs"}
                </button>
                <button
                  onClick={() => handleEdit(wf)}
                  className="bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 rounded-md text-xs font-medium"
                >
                  Update
                </button>
                <button
                  onClick={() => handleDelete(wf._id)}
                  className="text-red-600 border border-red-200 px-3 py-1.5 rounded-md text-xs font-medium"
                >
                  Delete
                </button>
              </div>
            </div>

            {expandedId === wf._id && (
              <div className="mt-4 border-t pt-3">
                <h4 className="text-xs font-semibold text-gray-500 mb-2">Recent Executions</h4>
                {(executions[wf._id] || []).length === 0 && (
                  <p className="text-xs text-gray-400">No runs yet.</p>
                )}
                <ul className="space-y-1">
                  {(executions[wf._id] || []).map((ex) => (
                    <li key={ex._id} className="text-xs flex items-center gap-2">
                      <span
                        className={`px-2 py-0.5 rounded-full ${
                          ex.status === "success"
                            ? "bg-green-100 text-green-700"
                            : ex.status === "failed"
                            ? "bg-red-100 text-red-700"
                            : "bg-yellow-100 text-yellow-700"
                        }`}
                      >
                        {ex.status}
                      </span>
                      <span className="text-gray-500">
                        {new Date(ex.createdAt).toLocaleString()} · {ex.durationMs || 0}ms
                      </span>
                      {ex.error && <span className="text-red-500">— {ex.error}</span>}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default WhatsAppWorkflows;
